import { axiosClient } from '../api/axiosClient';
import { AxiosError } from 'axios';
import { OrgCredential } from '../interfaces/organization';
import { handleOAuthResponse } from './oauthService';
import { buildOAuthUrl, createOAuthPopup, pingOrg } from './oauthService';
import { fetchAuthParams } from './oauthService';
import { Notify } from './notificationService';
import { trackException } from '../utils/monitoring';
import { CirraServiceType } from '../enums/auth';
import ActionError from '../utils/ActionError';
import { isNetworkError } from '../utils/errorHandling';

export const fetchCredentials = async (): Promise<OrgCredential[]> => {
  try {
    const response = await axiosClient.get('/credentials');
    let _credentials = response.data;
    if (!_credentials.length) {
      return [];
    }

    // Get the first credential and verify it
    const firstCredential = _credentials[0];
    const isActive = await verifyCredential(firstCredential);

    if (!isActive) {
      // Fetch latest credentials after pinging credential status might have changed
      const updatedResponse = await axiosClient.get('/credentials');
      _credentials = updatedResponse.data;
    }

    return _credentials;
  } catch (error) {
    const axiosError = error as AxiosError;

    // Handle network errors gracefully
    if (isNetworkError(axiosError)) {
      // Return empty array for network errors instead of throwing
      // This allows the app to continue functioning when server is down
      console.warn('Unable to connect to server. Operating in offline mode.');
      return [];
    }

    // Throw ActionError for proper serialization
    const message =
      (axiosError.response?.data as { message?: string })?.message ||
      'Failed to fetch credentials!';
    throw new ActionError(message, axiosError.response?.status, 'API_ERROR');
  }
};

export const selectCredential = async (
  cred: OrgCredential,
  service: CirraServiceType
): Promise<OrgCredential[]> => {
  try {
    // First select the credential
    await axiosClient.get(`/credentials/${cred.id}/select?service=${service}`);
    // Then fetch the whole collection to ensure we have the latest state
    return fetchCredentials();
  } catch (error) {
    const axiosError = error as AxiosError;
    throw new Error(
      (axiosError.response?.data as { message?: string })?.message ||
        'Failed to select credentials!'
    );
  }
};

export const deleteCredential = async (cred: OrgCredential): Promise<void> => {
  try {
    await axiosClient.delete(`/credentials/${cred.id}`);
  } catch (error) {
    const axiosError = error as AxiosError;
    Notify.error(
      (axiosError.response?.data as { message?: string })?.message ||
        'Failed to delete credentials!'
    );
    throw error;
  }
};

export const verifyCredential = async (
  cred: OrgCredential
): Promise<boolean> => {
  try {
    const response = await pingOrg(cred);
    return !!response.data;
  } catch (error) {
    // 400 errors are expected when credentials are stale/invalid/revoked
    // This is not an error - it's the verification working correctly
    console.debug(
      `[verifyCredential] Credential verification failed for org ${cred.organization_id} (expected for stale/invalid credentials)`,
      error
    );
    return false;
  }
};

export interface RefreshCredentialsResult {
  success: boolean;
  credentialId?: string;
}

export const refreshCredentials = async (
  selectedCredential: OrgCredential,
  service: CirraServiceType
): Promise<RefreshCredentialsResult> => {
  try {
    const authParams = await fetchAuthParams({
      orgInfo: selectedCredential,
      service,
    });

    const baseUrl = selectedCredential.hostname.startsWith('https://')
      ? selectedCredential.hostname
      : `https://${selectedCredential.hostname}`;

    const oauthUrl = buildOAuthUrl({
      authParams,
      url: baseUrl,
      loginHint: selectedCredential.username,
    });

    return new Promise((resolve, reject) => {
      const popup = createOAuthPopup(
        oauthUrl,
        (event: MessageEvent) => {
          handleOAuthResponse(
            event,
            async (result) => {
              resolve({
                success: true,
                credentialId: result.credentialId,
              });
            },
            (error) => {
              reject(error); // Reject the promise on authentication error
            }
          );
        },
        () => {
          resolve({ success: false }); // Resolve false if the popup is closed by the user
        }
      );

      // Ensure popup was successfully created, otherwise resolve false
      if (!popup) {
        resolve({ success: false });
      }
    });
  } catch (error) {
    trackException(new Error('Error refreshing credentials:' + error));
    throw error; // Rethrow the error for the caller to handle
  }
};
